import React from "react";
import { Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "../../atoms/button";

export const Panel = ({
  header = true,
  title,
  description,
  loading,
  open,
  closePanel,
  save,
  footer = true,
  children,
  buttonText = "Save",
  size,
  container,
}: {
  header?: boolean;
  title?: string;
  description?: string;
  loading?: boolean;
  open: boolean;
  openPanel: () => void;
  closePanel: () => void;
  save?: () => void;
  footer?: boolean;
  buttonText?: string;
  children: React.ReactNode;
  size?: string;
  container?: string;
}) => {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={closePanel}>
        <TransitionChild
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-800 bg-opacity-75 transition-opacity" />
        </TransitionChild>
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full">
                <DialogPanel
                  className={`pointer-events-auto w-screen ${size === "lg" ? "max-w-[52rem]" : size === "xl" ? "max-w-[72rem]" : "max-w-2xl"}`}>
                  <div
                    className={`h-full bg-white shadow-xl ${!container && "flex flex-col divide-y divide-gray-200"}`}>
                    {/* Panel Header */}
                    {header && (
                      <div className="bg-white px-6 lg:px-8 py-5 md:py-3.5 max-sm:border-b">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <DialogTitle className="text-base font-semibold leading-6 text-dark">
                              {title}
                            </DialogTitle>
                            <p className="font-medium text-[15px] text-gray-700">
                              {description}
                            </p>
                          </div>
                          {/* Close Button */}
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md bg-white text-gray-600 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-white"
                              onClick={() => closePanel()}>
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Panel Body */}
                    <div
                      className={`${container ? container : "bg-white py-5 px-6 lg:px-8 flex h-full flex-col overflow-y-scroll"}`}>
                      {children}
                    </div>
                    {/* Panel Footer */}
                    {footer && (
                      <div className="flex space-x-3 justify-end px-4 py-4">
                        <Button outline onClick={() => closePanel()}>
                          Cancel
                        </Button>
                        <Button
                          loading={loading}
                          disabled={loading}
                          onClick={() => save?.()}>
                          {buttonText}
                        </Button>
                      </div>
                    )}
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
